import { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Lock, ArrowRight, Download, CheckCircle2, ArrowRightLeft,
  MonitorSmartphone, CalendarDays, Users, CreditCard, KeyRound,
  Building2, QrCode, MessageSquare, BookOpen
} from "lucide-react";

const PASSCODE = "TheRig2026";

const COMPARISONS = [
  {
    area: "Signing In",
    icon: KeyRound,
    spacebring: "Email + password via Spacebring app or web portal",
    therig: "Email + password OR one-time passcode (OTP) sent to your email — no password needed",
  },
  {
    area: "Booking a Room",
    icon: CalendarDays,
    spacebring: "Locations > Rooms > select date & time slots in calendar view",
    therig: "Spaces > pick a room > choose date, time, and layout > confirm. Same simplicity, fresh interface",
  },
  {
    area: "Viewing Your Bookings",
    icon: BookOpen,
    spacebring: "Account > Bookings section in the app",
    therig: "My Dashboard > My Bookings tab — see upcoming, past, and cancelled bookings in one place",
  },
  {
    area: "Plan & Hours",
    icon: CreditCard,
    spacebring: "Credits or membership plans managed by admin",
    therig: "Your plan hours are visible on your dashboard. Hours are shared across your team and tracked automatically",
  },
  {
    area: "Calendar Sync",
    icon: CalendarDays,
    spacebring: "Two-way Google Calendar sync",
    therig: "Add bookings to Google Calendar, Outlook, or any app via iCal. Confirmation emails include calendar invite attachments",
  },
  {
    area: "Room Displays",
    icon: QrCode,
    spacebring: "Tablet-based Room Display app with color-coded status",
    therig: "E-ink friendly display screens per room showing live status + QR code to book directly when available",
  },
  {
    area: "Mobile Access",
    icon: MonitorSmartphone,
    spacebring: "Dedicated iOS/Android app",
    therig: "Fully responsive web app — works on any phone browser, no download needed. Bookmark it for quick access",
  },
  {
    area: "Cancellations",
    icon: ArrowRightLeft,
    spacebring: "Cancel via app with admin-configured policies",
    therig: "Cancel anytime from your dashboard. Paid bookings cancelled less than 12 hours before require admin approval",
  },
  {
    area: "Team Management",
    icon: Users,
    spacebring: "Admin manages members through the portal",
    therig: "Managers add team members directly — they receive credentials by email automatically. Members can book rooms for each other",
  },
  {
    area: "Contacting Admin",
    icon: MessageSquare,
    spacebring: "Email or in-app support",
    therig: "Built-in Messages tab on your dashboard — direct communication with The Rig admin team",
  },
];

const STEPS = [
  {
    num: "1",
    title: "Check your email",
    desc: "You'll receive a welcome email, a separate email with your login credentials, and a getting-started guide with first steps.",
  },
  {
    num: "2",
    title: "Sign in to The Rig",
    desc: "Go to the login page and use your emailed credentials. Or click \"Sign in with passcode\" to get a one-time code sent to your email — no password to remember.",
  },
  {
    num: "3",
    title: "Explore your dashboard",
    desc: "Everything you need is on your dashboard: bookings, plan hours, messages, and your profile settings.",
  },
  {
    num: "4",
    title: "Book your first room",
    desc: "Go to Spaces, pick a room, choose your date and time, and confirm. It's that simple.",
  },
  {
    num: "5",
    title: "Update your profile",
    desc: "Visit Profile Settings to update your name, phone, and change your password if you'd like.",
  },
];

const FAQS = [
  {
    q: "Do I keep my existing bookings from Spacebring?",
    a: "Existing bookings have been migrated. Check your dashboard under \"My Bookings\" to confirm everything transferred correctly. If anything is missing, message the admin directly from the platform.",
  },
  {
    q: "Can I still use the Spacebring app?",
    a: "The Rig is replacing Spacebring as your booking platform. The Spacebring app will no longer be active. Please use The Rig's web platform going forward.",
  },
  {
    q: "Is there a mobile app?",
    a: "The Rig is a fully responsive web application — it works great on your phone's browser. Tip: add it to your home screen for an app-like experience.",
  },
  {
    q: "What if I forgot my password?",
    a: "No problem! On the login page, click \"Sign in with passcode\" to receive a one-time code by email. You can also change your password anytime from Profile Settings.",
  },
  {
    q: "How do I add my team members?",
    a: "If you're a Manager, go to your dashboard's Team tab and add members. They'll automatically receive welcome and credential emails.",
  },
  {
    q: "Who do I contact if I have issues?",
    a: "Use the Messages tab on your dashboard to reach the admin team directly. You can also email therig@ic-foundation.org.",
  },
];

function PrintableGuide() {
  return (
    <div id="printable-guide" className="hidden print:block bg-white text-black p-8 max-w-[800px] mx-auto" style={{ fontFamily: "Arial, sans-serif" }}>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 4 }}>Transition Guide</h1>
        <p style={{ fontSize: 14, color: "#666" }}>Moving from Spacebring to The Rig</p>
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, borderBottom: "2px solid #1E1E1E", paddingBottom: 4 }}>Getting Started</h2>
      {STEPS.map(s => (
        <div key={s.num} style={{ marginBottom: 12, paddingLeft: 8 }}>
          <p style={{ fontWeight: 700, fontSize: 14 }}>{s.num}. {s.title}</p>
          <p style={{ fontSize: 13, color: "#444", marginTop: 2 }}>{s.desc}</p>
        </div>
      ))}

      <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 12, borderBottom: "2px solid #1E1E1E", paddingBottom: 4 }}>What's Different</h2>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd", width: "20%" }}>Feature</th>
            <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd", width: "40%" }}>Spacebring</th>
            <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd", width: "40%" }}>The Rig</th>
          </tr>
        </thead>
        <tbody>
          {COMPARISONS.map(c => (
            <tr key={c.area}>
              <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee", fontWeight: 600 }}>{c.area}</td>
              <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee", color: "#666" }}>{c.spacebring}</td>
              <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{c.therig}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 12, borderBottom: "2px solid #1E1E1E", paddingBottom: 4 }}>FAQ</h2>
      {FAQS.map((f, i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <p style={{ fontWeight: 700, fontSize: 13 }}>{f.q}</p>
          <p style={{ fontSize: 12, color: "#444", marginTop: 2 }}>{f.a}</p>
        </div>
      ))}

      <div style={{ marginTop: 32, textAlign: "center", color: "#999", fontSize: 11 }}>
        The Rig Coworking Space | therig@ic-foundation.org
      </div>
    </div>
  );
}

export default function TransitionGuide() {
  const [unlocked, setUnlocked] = useState(false);
  const [code, setCode] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "Transition Guide | The Rig";
    if (sessionStorage.getItem("transition_unlocked") === "1") setUnlocked(true);
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (code === PASSCODE) {
      setUnlocked(true);
      sessionStorage.setItem("transition_unlocked", "1");
    } else {
      toast.error("Incorrect passcode");
      setCode("");
    }
  };

  const handleDownload = () => {
    const printWindow = window.open("", "_blank");
    const content = `<!DOCTYPE html><html><head><title>Transition Guide - The Rig</title>
    <style>
      body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 32px; color: #1E1E1E; }
      h1 { font-size: 28px; text-align: center; margin-bottom: 4px; }
      .subtitle { text-align: center; color: #666; font-size: 14px; margin-bottom: 32px; }
      h2 { font-size: 18px; border-bottom: 2px solid #1E1E1E; padding-bottom: 4px; margin-top: 28px; }
      .step { margin-bottom: 12px; padding-left: 8px; }
      .step-title { font-weight: 700; font-size: 14px; }
      .step-desc { font-size: 13px; color: #444; margin-top: 2px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 8px; }
      th { text-align: left; padding: 8px; background: #f5f5f5; border-bottom: 1px solid #ddd; }
      td { padding: 6px 8px; border-bottom: 1px solid #eee; }
      .faq-q { font-weight: 700; font-size: 13px; margin-bottom: 2px; }
      .faq-a { font-size: 12px; color: #444; margin-bottom: 14px; }
      .footer { margin-top: 32px; text-align: center; color: #999; font-size: 11px; }
      @media print { body { padding: 16px; } }
    </style></head><body>
    <h1>Transition Guide</h1>
    <p class="subtitle">Moving from Spacebring to The Rig</p>
    <h2>Getting Started</h2>
    ${STEPS.map(s => `<div class="step"><p class="step-title">${s.num}. ${s.title}</p><p class="step-desc">${s.desc}</p></div>`).join("")}
    <h2>What's Different</h2>
    <table><thead><tr><th style="width:20%">Feature</th><th style="width:40%">Spacebring</th><th style="width:40%">The Rig</th></tr></thead><tbody>
    ${COMPARISONS.map(c => `<tr><td style="font-weight:600">${c.area}</td><td style="color:#666">${c.spacebring}</td><td>${c.therig}</td></tr>`).join("")}
    </tbody></table>
    <h2>FAQ</h2>
    ${FAQS.map(f => `<p class="faq-q">${f.q}</p><p class="faq-a">${f.a}</p>`).join("")}
    <p class="footer">The Rig Coworking Space | therig@ic-foundation.org</p>
    </body></html>`;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
  };

  if (!unlocked) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4" data-testid="transition-gate">
        <title>Transition Guide | The Rig</title>
        <Card className="w-full max-w-sm border-border rounded-none">
          <CardContent className="pt-8 pb-6 px-6">
            <div className="text-center mb-6">
              <Lock className="h-8 w-8 mx-auto text-rig-charcoal/30 mb-3" />
              <h2 className="font-heading font-bold text-lg text-rig-charcoal">Transition Guide</h2>
              <p className="text-xs text-muted-foreground mt-1">Enter the passcode to access</p>
            </div>
            <form onSubmit={handleUnlock} className="space-y-4">
              <Input
                type="password"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="Passcode"
                autoFocus
                data-testid="transition-passcode-input"
              />
              <button
                type="submit"
                className="w-full bg-rig-charcoal text-white py-2.5 text-sm font-semibold hover:bg-rig-charcoal/90 transition-colors"
                data-testid="transition-unlock-btn"
              >
                Unlock
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="transition-guide-page">
      <title>Transition Guide | The Rig</title>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-10">
        <div>
          <Badge className="bg-rig-orange text-white border-0 mb-3">Transition</Badge>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-rig-charcoal mb-2">
            Moving to The Rig
          </h1>
          <p className="text-rig-charcoal/60 text-base max-w-xl">
            Everything you need to switch from Spacebring to your new booking platform.
          </p>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 bg-rig-charcoal text-white px-5 py-2.5 text-sm font-semibold hover:bg-rig-charcoal/90 transition-colors flex-shrink-0 self-start"
          data-testid="download-guide-btn"
        >
          <Download className="h-4 w-4" />
          Download / Print
        </button>
      </div>

      {/* Getting Started Steps */}
      <section className="mb-12" data-testid="transition-steps-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-6">Getting Started</h2>
        <div className="space-y-4">
          {STEPS.map(s => (
            <div key={s.num} className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-9 h-9 bg-rig-charcoal text-[#FFD60A] rounded-full flex items-center justify-center text-sm font-bold">
                {s.num}
              </div>
              <div className="pt-1">
                <h3 className="font-heading font-bold text-sm text-rig-charcoal">{s.title}</h3>
                <p className="text-sm text-rig-charcoal/60 mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Separator className="mb-12" />

      {/* Comparison Table */}
      <section className="mb-12" data-testid="transition-comparison-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-2">What's Different</h2>
        <p className="text-sm text-rig-charcoal/60 mb-6">
          Side-by-side comparison of what you're used to and where to find it now.
        </p>
        <div className="space-y-3">
          {COMPARISONS.map(c => {
            const Icon = c.icon;
            return (
              <Card key={c.area} className="border-border rounded-none overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-center gap-3 px-4 py-3 bg-rig-charcoal/[0.03] border-b border-border">
                    <Icon className="h-4 w-4 text-rig-teal flex-shrink-0" />
                    <h3 className="font-heading font-bold text-sm text-rig-charcoal">{c.area}</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">
                    <div className="px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-rig-charcoal/30 mb-1">Spacebring</p>
                      <p className="text-xs text-rig-charcoal/60">{c.spacebring}</p>
                    </div>
                    <div className="px-4 py-3 bg-rig-cream/40">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-rig-teal mb-1">The Rig</p>
                      <p className="text-xs text-rig-charcoal/80">{c.therig}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <Separator className="mb-12" />

      {/* Key Improvements */}
      <section className="mb-12" data-testid="transition-improvements-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-6">What's New</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { title: "Passwordless Sign-In", desc: "Sign in with a one-time passcode — no password to remember or reset." },
            { title: "Built-In Messaging", desc: "Contact admin directly from your dashboard. No external emails needed." },
            { title: "Auto Welcome Emails", desc: "New members get credentials and a getting-started guide automatically." },
            { title: "Role-Specific Guides", desc: "In-app guides for Admins, Managers, and Members — always accessible." },
            { title: "E-Ink Room Displays", desc: "Always-on screens outside each room with live status and QR booking." },
            { title: "Profile Self-Service", desc: "Update your name, phone, and password anytime from Profile Settings." },
            { title: "Book for Teammates", desc: "Managers and members can book rooms on behalf of their colleagues. Both parties get confirmation emails." },
          ].map(item => (
            <div key={item.title} className="flex items-start gap-3 p-4 bg-rig-cream/50 border border-rig-yellow/20 rounded-lg">
              <CheckCircle2 className="h-4 w-4 text-rig-green flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-rig-charcoal">{item.title}</h4>
                <p className="text-xs text-rig-charcoal/60 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Separator className="mb-12" />

      {/* FAQ */}
      <section className="mb-12" data-testid="transition-faq-section">
        <h2 className="text-xl font-heading font-bold text-rig-charcoal mb-6">Frequently Asked Questions</h2>
        <div className="space-y-2">
          {FAQS.map((f, i) => (
            <div key={i} className="border border-border rounded-none overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-rig-charcoal/[0.02] transition-colors"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="text-sm font-medium text-rig-charcoal pr-4">{f.q}</span>
                <ArrowRight className={`h-4 w-4 flex-shrink-0 text-rig-charcoal/30 transition-transform ${openFaq === i ? "rotate-90" : ""}`} />
              </button>
              {openFaq === i && (
                <div className="px-4 pb-3">
                  <p className="text-sm text-rig-charcoal/60">{f.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Help CTA */}
      <Card className="border-border rounded-none bg-rig-charcoal text-white" data-testid="transition-help-card">
        <CardContent className="py-8 px-6 text-center">
          <h3 className="font-heading font-bold text-lg">Need Help?</h3>
          <p className="text-white/60 text-sm mt-1 mb-4 max-w-md mx-auto">
            If you run into any issues during the transition, our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="mailto:therig@ic-foundation.org" className="inline-flex items-center justify-center gap-2 bg-[#FFD60A] text-rig-charcoal px-5 py-2.5 text-sm font-bold hover:bg-[#FFD60A]/90 transition-colors">
              <MessageSquare className="h-4 w-4" /> Email Us
            </a>
            <a href="/login" className="inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-2.5 text-sm font-medium hover:bg-white/10 transition-colors">
              Sign In Now <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 text-center text-xs text-rig-charcoal/30">
        The Rig Transition Guide — For internal distribution
      </div>
    </div>
  );
}
